import React, { useState, useEffect } from 'react';
import { BarChart3, Users, FileText, Star, Heart, RefreshCw, Download, Edit2, Trash2, MessageSquare, Search, Upload, X, Image as ImageIcon } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { supabase } from '../../lib/supabase';
import { CandidateWithEvaluations } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

interface EvaluationStats {
  totalEvaluations: number;
  averageRating: number;
  totalLikes: number;
  totalParticipationInterest: number;
}

export default function AdminDashboard() {
  const [candidates, setCandidates] = useState<CandidateWithEvaluations[]>([]);
  const [stats, setStats] = useState<EvaluationStats>({
    totalEvaluations: 0,
    averageRating: 0,
    totalLikes: 0,
    totalParticipationInterest: 0
  });
  const [evaluatorTotal, setEvaluatorTotal] = useState<number>(0);
  const [commentCountsByCandidate, setCommentCountsByCandidate] = useState<Record<string, number>>({});
  const [trendingCommentCounts, setTrendingCommentCounts] = useState<Record<string, number>>({});
  const [timeSeries, setTimeSeries] = useState<{ ts: string; comments: number; likes: number; participates: number; ratings: number; views: number }[]>([]);
  const [timeRangeHours, setTimeRangeHours] = useState<number>(3);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [hotByCandidate, setHotByCandidate] = useState<{ id: string; name: string; topic: string; comments: number; likes: number; participates: number; ratings: number; views: number; total: number }[]>([]);
  const [viewsByCandidate, setViewsByCandidate] = useState<Record<string, number>>({});
  const [evaluatorById, setEvaluatorById] = useState<Record<string, { name: string }>>({});
  const [evalToEvaluator, setEvalToEvaluator] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCandidateId, setOpenCandidateId] = useState<string | null>(null);
  const [candidateEdits, setCandidateEdits] = useState<Record<string, any>>({});
  const [evaluatorEdits, setEvaluatorEdits] = useState<Record<string, any>>({});
  const [evaluatorIds, setEvaluatorIds] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<'users' | 'participants' | 'stats' | 'comments'>('users');
  const [participants, setParticipants] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [groupedComments, setGroupedComments] = useState<Record<string, Record<string, any[]>>>({});
  const [openCommentsForCandidate, setOpenCommentsForCandidate] = useState<string | null>(null);
  const [commentSearch, setCommentSearch] = useState('');
  const [slideUploading, setSlideUploading] = useState<Record<string, boolean>>({});
  const cancelledRef = React.useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    loadDashboardData();
    return () => { cancelledRef.current = true; };
  }, []);

  const loadDashboardData = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      // Load candidates with evaluations
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select(`
          *,
          slide_images (*),
          evaluations (*)
        `)
        .order('created_at', { ascending: false });

      if (isCancelled()) return;
      if (candidatesError) throw candidatesError;
      setCandidates(candidatesData || []);

      // Calculate overall stats
      const allEvaluations = candidatesData?.flatMap(c => c.evaluations) || [];
      const rated = allEvaluations.filter((e: any) => (e.rating || 0) > 0);
      const totalEvaluations = rated.length; // 星を付けた人数
      const averageRating = totalEvaluations > 0 
        ? rated.reduce((sum: number, e: any) => sum + Math.min(5, Math.max(0, e.rating || 0)), 0) / totalEvaluations 
        : 0;
      const totalLikes = allEvaluations.filter(evaluation => evaluation.liked).length;
      const totalParticipationInterest = allEvaluations.filter(evaluation => evaluation.want_to_participate).length;

      setStats({
        totalEvaluations,
        averageRating,
        totalLikes,
        totalParticipationInterest
      });

      // Evaluator total count
      try {
        const { count } = await supabase.from('evaluators').select('*', { count: 'exact', head: true });
        if (isCancelled()) return;
        setEvaluatorTotal(count || 0);
      } catch {}

      // Build comment counts and grouping by candidate
      try {
        const evalToCand: Record<string, string> = {};
        const evalToEvalMap: Record<string, string> = {};
        const allEvalIds: string[] = [];
        const allEvaluatorIds: Set<string> = new Set();
        (candidatesData || []).forEach((c: any) => {
          (c.evaluations || []).forEach((e: any) => {
            evalToCand[e.id] = c.id;
            evalToEvalMap[e.id] = e.evaluator_id;
            if (e.evaluator_id) allEvaluatorIds.add(e.evaluator_id);
            allEvalIds.push(e.id);
          });
        });
        let byCand: Record<string, number> = {};
        let grouped: Record<string, Record<string, any[]>> = {};
        if (allEvalIds.length) {
          const { data: cms } = await supabase
            .from('evaluation_comments')
            .select('*')
            .in('evaluation_id', allEvalIds);
          if (isCancelled()) return;
          (cms || []).forEach((cm: any) => {
            const candId = evalToCand[cm.evaluation_id];
            if (!candId) return;
            byCand[candId] = (byCand[candId] || 0) + 1;
            const topic = cm.topic || 'general';
            grouped[candId] = grouped[candId] || {};
            grouped[candId][topic] = grouped[candId][topic] || [];
            grouped[candId][topic].push(cm);
          });
        }
        setCommentCountsByCandidate(byCand);
        setGroupedComments(grouped);
        setEvalToEvaluator(evalToEvalMap);

        // Load evaluator display names
        if (allEvaluatorIds.size > 0) {
          const ids = Array.from(allEvaluatorIds);
          const { data: infos } = await supabase
            .from('evaluators')
            .select('id, name')
            .in('id', ids);
          if (isCancelled()) return;
          const map: Record<string, { name: string }> = {};
          (infos || []).forEach((ev: any) => {
            map[ev.id] = { name: ev.name };
          });
          setEvaluatorById(map);
        } else {
          setEvaluatorById({});
        }
      } catch {}

      // Optional: page views per candidate (requires candidate_views table)
      try {
        const ids = (candidatesData || []).map((c: any) => c.id);
        if (ids.length) {
          const { data: views } = await supabase
            .from('candidate_views')
            .select('candidate_id')
            .in('candidate_id', ids);
          if (isCancelled()) return;
          const map: Record<string, number> = {};
          (views || []).forEach((v: any) => { map[v.candidate_id] = (map[v.candidate_id] || 0) + 1; });
          setViewsByCandidate(map);
        }
      } catch {
        if (!isCancelled()) setViewsByCandidate({});
      }
    } catch (err: any) {
      if (!isCancelled()) setError(err.message);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  const exportToCSV = () => {
    // CSVヘッダー
    const headers = [
      '研究者名',
      'ユーザー名',
      '所属',
      '年齢',
      '研究課題',
      '研究概要',
      '共同研究者',
      '評価数',
      '平均評価',
      'いいね数',
      '参加希望数',
      '登録日時'
    ];

    // CSVデータ作成
    const csvData = candidates.map(candidate => {
      const candidateStats = getCandidateStats(candidate);
      return [
        `"${candidate.name}"`,
        `"${candidate.name}"`,
        `"${candidate.affiliation}"`,
        candidate.age,
        `"${candidate.research_topic}"`,
        `"${candidate.research_overview.replace(/"/g, '""')}"`, // ダブルクォートをエスケープ
        candidate.has_collaborators ? 'あり' : 'なし',
        candidateStats.evaluationCount,
        candidateStats.averageRating.toFixed(1),
        candidateStats.likes,
        candidateStats.participationInterest,
        `"${new Date(candidate.created_at).toLocaleString('ja-JP')}"`
      ];
    });

    // CSV文字列作成
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');

    // BOMを追加してExcelで文字化けを防ぐ
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });

    // ダウンロード実行
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `研究発表評価結果_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportEvaluationsCSV = () => {
    // CSVヘッダー（候補者＋評価（参加者）明細）
    const headers = [
      '候補者名',
      '候補者ユーザー名',
      '所属',
      '研究課題',
      '参加者名',
      '参加者ユーザー名',
      '参加者所属',
      '評価(0-5)',
      'いいね',
      '参加希望',
      '評価作成日時'
    ];

    const rows: string[][] = [];
    (candidates || []).forEach((cand: any) => {
      (cand.evaluations || []).forEach((ev: any) => {
        const info = ev.evaluator_id ? (evaluatorById[ev.evaluator_id] || {}) : {} as any;
        rows.push([
          `"${cand.name}"`,
          `"${cand.name}"`,
          `"${cand.affiliation || ''}"`,
          `"${cand.research_topic || ''}"`,
          `"${info.name || ''}"`,
          `"${info.name}"`,
          `"${(info as any).affiliation || ''}"`,
          String(Math.max(0, Math.min(5, ev.rating || 0))),
          ev.liked ? '1' : '0',
          ev.want_to_participate ? '1' : '0',
          `"${ev.created_at ? new Date(ev.created_at).toLocaleString('ja-JP') : ''}"`
        ]);
      });
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `評価明細_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportEvaluatorsCSV = async () => {
    try {
      const { data: evalrs, error } = await supabase.from('evaluators').select('*').order('created_at', { ascending: true });
      if (error) throw error;
      const evaluators = evalrs || [];

      const { data: evaluations } = await supabase
        .from('evaluations')
        .select('id, evaluator_id, candidate_id, liked, want_to_participate');
      const { data: comments } = await supabase
        .from('evaluation_comments')
        .select('evaluation_id');

      const likesByEv: Record<string, number> = {};
      const partsByEv: Record<string, string[]> = {};
      const evalIdToEv: Record<string, string> = {};
      const candIds = new Set<string>();
      (evaluations || []).forEach((r: any) => {
        evalIdToEv[r.id] = r.evaluator_id;
        if (r.liked) likesByEv[r.evaluator_id] = (likesByEv[r.evaluator_id] || 0) + 1;
        if (r.want_to_participate) {
          (partsByEv[r.evaluator_id] = partsByEv[r.evaluator_id] || []).push(r.candidate_id);
          candIds.add(r.candidate_id);
        }
      });
      const cmByEv: Record<string, number> = {};
      (comments || []).forEach((c: any) => {
        const ev = evalIdToEv[c.evaluation_id];
        if (ev) cmByEv[ev] = (cmByEv[ev] || 0) + 1;
      });
      let topicByCand: Record<string, string> = {};
      if (candIds.size) {
        const { data: cands } = await supabase.from('candidates').select('id, research_topic').in('id', Array.from(candIds));
        (cands || []).forEach((c: any) => { topicByCand[c.id] = c.research_topic || ''; });
      }

      const headers = [
        '参加者名','ユーザー名','メールアドレス','所属','系・部署・診療科','大学院生','課程','学年','登録日時',
        'コメント数','いいね数','参加希望数','参加希望研究課題名'
      ];

      const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = evaluators.map((e: any) => {
        const partIds = partsByEv[e.id] || [];
        const partTopics = partIds.map(id => topicByCand[id]).filter(Boolean);
        return [
          esc(e.name),
          esc(e.name),
          esc(e.email || ''),
          esc(e.affiliation || ''),
          esc(e.department || ''),
          e.is_grad_student ? 'はい' : 'いいえ',
          esc(e.grad_program || ''),
          esc(e.grad_year || ''),
          esc(e.created_at ? new Date(e.created_at).toLocaleString('ja-JP') : ''),
          String(cmByEv[e.id] || 0),
          String(likesByEv[e.id] || 0),
          String(partIds.length),
          esc(partTopics.join('; '))
        ];
      });

      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `参加者一覧_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const getCandidateStats = (candidate: CandidateWithEvaluations) => {
    const evaluations = candidate.evaluations || [];
    const rated = evaluations.filter((e: any) => (e.rating || 0) > 0);
    const evaluationCount = rated.length; // 星を付けた人数
    const averageRating = evaluationCount > 0 
      ? rated.reduce((sum: number, e: any) => sum + Math.min(5, Math.max(0, e.rating || 0)), 0) / evaluationCount 
      : 0;
    const likes = evaluations.filter(evaluation => evaluation.liked).length;
    const participationInterest = evaluations.filter(evaluation => evaluation.want_to_participate).length;

    return {
      evaluationCount,
      averageRating,
      likes,
      participationInterest
    };
  };

  const getKeySlideUrl = (candidate: CandidateWithEvaluations) => {
    const keySlide = candidate.slide_images?.find(
      (slide) => slide.slide_index === candidate.key_slide_index
    );
    return keySlide?.image_url || candidate.slide_images?.[0]?.image_url;
  };

  const updateEvaluationField = async (evaluationId: string, patch: any) => {
    const { error } = await supabase.from('evaluations').update(patch).eq('id', evaluationId);
    if (error) setError(error.message); else await loadDashboardData();
  };

  const handleDeleteUser = async (email: string) => {
    const ok = window.confirm(
      `ユーザー「${email}」を削除します。\n\nこの操作は元に戻せません。関連する評価・コメント・スライド画像・認証ユーザーも削除されます。\n\n本当に削除してよろしいですか？`
    );
    if (!ok) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('セッションが無効です');
      const resp = await fetch('/api/admin-delete-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email })
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j.error || '削除に失敗しました');
      await loadDashboardData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const loadEvaluatorFor = async (candidateEmail: string, candidateId: string) => {
    const { data } = await supabase.from('evaluators').select('*').eq('email', candidateEmail).maybeSingle();
    if (data) {
      setEvaluatorIds((prev) => ({ ...prev, [candidateId]: data.id }));
      setEvaluatorEdits((prev) => ({
        ...prev,
        [candidateId]: {
          name: data.name || '',

          affiliation: (data as any).affiliation || '',
          department: (data as any).department || '',
          is_grad_student: !!(data as any).is_grad_student,
          grad_program: (data as any).grad_program || '',
          grad_year: (data as any).grad_year || ''
        }
      }));
    }
  };

  const openEditor = async (cand: any) => {
    setOpenCandidateId(openCandidateId === cand.id ? null : cand.id);
    if (openCandidateId !== cand.id) {
      // preload forms
      setCandidateEdits((prev) => ({
        ...prev,
        [cand.id]: {
          name: cand.name || '',

          affiliation: cand.affiliation || '',
          department: cand.department || '',
          research_topic: cand.research_topic || '',
          research_overview: cand.research_overview || '',
          is_grad_student: !!cand.is_grad_student,
          grad_program: cand.grad_program || '',
          grad_year: cand.grad_year ? String(cand.grad_year) : ''
        }
      }));
      await loadEvaluatorFor(cand.email, cand.id);
    }
  };

  const saveCandidateEdits = async (id: string) => {
    const vals = candidateEdits[id];
    const patch: any = {
      name: vals.name?.trim(),
      
      affiliation: vals.affiliation?.trim(),
      department: vals.department?.trim() || null,
      research_topic: vals.research_topic?.trim(),
      research_overview: vals.research_overview ?? '',
      is_grad_student: !!vals.is_grad_student,
      grad_program: vals.grad_program || null,
      grad_year: vals.grad_year ? parseInt(vals.grad_year) : null
    };
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('セッションが無効です');
      const resp = await fetch('/api/admin-update-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target: 'candidates', id, patch })
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j.error || '保存に失敗しました');
      await loadDashboardData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const saveEvaluatorEdits = async (candId: string) => {
    const eid = evaluatorIds[candId];
    const vals = evaluatorEdits[candId];
    if (!eid || !vals) return;
    const patch: any = {
      name: vals.name?.trim(),
      
      affiliation: vals.affiliation?.trim() || null,
      department: vals.department?.trim() || null,
      is_grad_student: !!vals.is_grad_student,
      grad_program: vals.grad_program || null,
      grad_year: vals.grad_year ? parseInt(vals.grad_year) : null
    };
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('セッションが無効です');
      const resp = await fetch('/api/admin-update-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target: 'evaluators', id: eid, patch })
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j.error || '保存に失敗しました');
      await loadDashboardData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // Admin: upload slide image for candidate
  const uploadSlideForCandidate = async (cand: any, slideIndex: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) { setError('画像ファイルを選択してください'); return; }
    const MAX_SLIDE_BYTES = 20 * 1024 * 1024;
    if (file.size > MAX_SLIDE_BYTES) { setError('画像サイズが大きすぎます (上限 20MB)'); return; }
    setSlideUploading((prev) => ({ ...prev, [cand.id]: true }));
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = `${cand.id}/slide_${slideIndex}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('slide-images')
        .upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage
        .from('slide-images')
        .getPublicUrl(fileName);
      // Upsert slide row via admin API (bypass RLS)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) throw new Error('セッションが無効です');
        const resp = await fetch('/api/admin-upsert-slide-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ candidateId: cand.id, slideIndex, imageUrl: publicUrl })
        });
        const j = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(j.error || 'スライドの登録に失敗しました');
      } catch (e: any) {
        throw e;
      }
      await loadDashboardData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSlideUploading((prev) => ({ ...prev, [cand.id]: false }));
    }
  };

  // Admin: set key slide index for candidate
  const setKeySlideForCandidate = async (candId: string, slideIndex: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('セッションが無効です');
      const resp = await fetch('/api/admin-set-key-slide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidateId: candId, slideIndex })
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j.error || 'キー設定に失敗しました');
      await loadDashboardData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const loadRecentComments = async () => {
    const { data } = await supabase
      .from('evaluation_comments')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    setComments(data || []);
  };

  // Load participants (evaluators) with metrics for Participants tab and CSV
  const loadParticipantsData = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      // base evaluators
      const { data: evalrs, error: e1 } = await supabase
        .from('evaluators')
        .select('*')
        .order('created_at', { ascending: true });
      if (isCancelled()) return;
      if (e1) throw e1;
      const evalsArr = evalrs || [];

      // evaluations for metrics
      const { data: evals } = await supabase
        .from('evaluations')
        .select('id, evaluator_id, candidate_id, liked, want_to_participate');
      if (isCancelled()) return;
      const evalIdsByEv: Record<string, string[]> = {};
      const likesByEv: Record<string, number> = {};
      const partsByEv: Record<string, { count: number; candidateIds: string[] }> = {};
      (evals || []).forEach((r: any) => {
        const evId = r.evaluator_id as string;
        (evalIdsByEv[evId] = evalIdsByEv[evId] || []).push(r.id);
        if (r.liked) likesByEv[evId] = (likesByEv[evId] || 0) + 1;
        if (r.want_to_participate) {
          const entry = (partsByEv[evId] = partsByEv[evId] || { count: 0, candidateIds: [] });
          entry.count += 1;
          entry.candidateIds.push(r.candidate_id);
        }
      });

      // comments counts by evaluator via evaluation_id grouping
      const { data: cms } = await supabase
        .from('evaluation_comments')
        .select('evaluation_id');
      if (isCancelled()) return;
      const cmCountByEvalId: Record<string, number> = {};
      (cms || []).forEach((row: any) => {
        const evId = (evals || []).find((e: any) => e.id === row.evaluation_id)?.evaluator_id;
        if (evId) cmCountByEvalId[evId] = (cmCountByEvalId[evId] || 0) + 1;
      });

      // fetch topics for participate lists
      const allPartCandIds = new Set<string>();
      Object.values(partsByEv).forEach(v => v.candidateIds.forEach(id => allPartCandIds.add(id)));
      let topicByCandId: Record<string, string> = {};
      if (allPartCandIds.size) {
        const { data: cands } = await supabase
          .from('candidates')
          .select('id, research_topic')
          .in('id', Array.from(allPartCandIds));
        if (isCancelled()) return;
        (cands || []).forEach((c: any) => { topicByCandId[c.id] = c.research_topic || ''; });
      }

      const rows = evalsArr.map((e: any) => {
        const evId = e.id as string;
        const likes = likesByEv[evId] || 0;
        const partInfo = partsByEv[evId] || { count: 0, candidateIds: [] };
        const partTopics = partInfo.candidateIds.map(cid => topicByCandId[cid]).filter(Boolean);
        const comments = cmCountByEvalId[evId] || 0;
        return {
          ...e,
          likesCount: likes,
          participateCount: partInfo.count,
          participateTopics: partTopics,
          commentsCount: comments,
        };
      });
      if (!isCancelled()) setParticipants(rows);
    } catch (e: any) {
      if (!isCancelled()) {
        setError(e.message);
        setParticipants([]);
      }
    }
  };

  useEffect(() => {
    if (tab === 'participants') {
      loadParticipantsData();
    }
  }, [tab]);

  // Build 5-min buckets between now - range and now
  const buildBuckets = (hours: number) => {
    const end = new Date();
    const start = new Date(end.getTime() - hours * 60 * 60 * 1000);
    const buckets: { key: string; from: number; to: number }[] = [];
    const step = 5 * 60 * 1000;
    const startMs = Math.floor(start.getTime() / step) * step;
    for (let t = startMs; t <= end.getTime(); t += step) {
      const from = t;
      const to = t + step;
      buckets.push({ key: new Date(from).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }), from, to });
    }
    return buckets;
  };

  // Load event logs and candidate views to build series
  const loadTimeSeries = async () => {
    const isCancelled = () => cancelledRef.current;
    setSeriesLoading(true);
    try {
      const hours = timeRangeHours;
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      // evaluation_events
      const { data: events } = await supabase
        .from('evaluation_events')
        .select('candidate_id, event_type, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: true });
      if (isCancelled()) return;
      // candidate_views (optional)
      let views: any[] = [];
      try {
        const { data: v } = await supabase
          .from('candidate_views')
          .select('candidate_id, created_at')
          .gte('created_at', since)
          .order('created_at', { ascending: true });
        if (isCancelled()) return;
        views = v || [];
      } catch {}

      const buckets = buildBuckets(hours);
      const series = buckets.map(b => ({ ts: b.key, comments: 0, likes: 0, participates: 0, ratings: 0, views: 0 }));
      const inBucket = (ms: number, from: number, to: number) => ms >= from && ms < to;
      // Aggregate events into time buckets
      (events || []).forEach((e: any) => {
        const ms = new Date(e.created_at as string).getTime();
        for (let i = 0; i < buckets.length; i++) {
          const b = buckets[i];
          if (inBucket(ms, b.from, b.to)) {
            if (e.event_type === 'comment') series[i].comments += 1;
            else if (e.event_type === 'like_on') series[i].likes += 1;
            else if (e.event_type === 'participate_on') series[i].participates += 1;
            else if (e.event_type === 'rating_change') series[i].ratings += 1;
            break;
          }
        }
      });
      // Aggregate views
      (views || []).forEach((v: any) => {
        const ms = new Date(v.created_at as string).getTime();
        for (let i = 0; i < buckets.length; i++) {
          const b = buckets[i];
          if (inBucket(ms, b.from, b.to)) { series[i].views += 1; break; }
        }
      });

      // Build per-candidate hotness
      const hotMap: Record<string, { comments: number; likes: number; participates: number; ratings: number; views: number }> = {};
      (events || []).forEach((e: any) => {
        const cid = e.candidate_id as string | undefined;
        if (!cid) return;
        const h = (hotMap[cid] = hotMap[cid] || { comments: 0, likes: 0, participates: 0, ratings: 0, views: 0 });
        if (e.event_type === 'comment') h.comments += 1;
        else if (e.event_type === 'like_on') h.likes += 1;
        else if (e.event_type === 'participate_on') h.participates += 1;
        else if (e.event_type === 'rating_change') h.ratings += 1;
      });
      (views || []).forEach((v: any) => {
        const cid = v.candidate_id as string | undefined;
        if (!cid) return;
        const h = (hotMap[cid] = hotMap[cid] || { comments: 0, likes: 0, participates: 0, ratings: 0, views: 0 });
        h.views += 1;
      });
      const rows: { id: string; name: string; topic: string; comments: number; likes: number; participates: number; ratings: number; views: number; total: number }[] = [];
      (Object.keys(hotMap)).forEach((cid) => {
        const c = (candidates as any[]).find(x => x.id === cid);
        const h = hotMap[cid];
        const total = h.comments + h.likes + h.participates + h.ratings + h.views;
        rows.push({ id: cid, name: c?.name || '', topic: c?.research_topic || '', ...h, total });
      });
      rows.sort((a, b) => b.total - a.total);
      if (!isCancelled()) {
        setTimeSeries(series);
        setHotByCandidate(rows);
      }
    } finally {
      if (!isCancelled()) setSeriesLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'stats') loadTimeSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, timeRangeHours]);

  // Stats signals: comments in the last 2 hours per candidate
  const loadStatsSignals = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      const evalToCand: Record<string, string> = {};
      (candidates || []).forEach((c: any) => {
        (c.evaluations || []).forEach((e: any) => { evalToCand[e.id] = c.id; });
      });
      const since = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('evaluation_comments')
        .select('evaluation_id, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false });
      if (isCancelled()) return;
      const map: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        const cid = evalToCand[row.evaluation_id];
        if (cid) map[cid] = (map[cid] || 0) + 1;
      });
      setTrendingCommentCounts(map);
    } catch {
      if (!isCancelled()) setTrendingCommentCounts({});
    }
  };

  useEffect(() => {
    if (tab === 'stats') {
      loadStatsSignals();
    }
  }, [tab, candidates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">管理ダッシュボード</h1>
          <p className="text-muted-foreground mt-1">評価結果の集計と分析（星0は評価なしとして集計対象外）</p>
        </div>
        <div className="flex space-x-3">
          <Button
            onClick={loadDashboardData}
            variant="outline"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            データを更新
          </Button>
        </div>
      </div>

      {/* Overall Stats */}
      {/* Tabs */}
      <div className="flex items-center gap-2">
        <Button variant={tab === 'users' ? 'default' : 'outline'} size="sm" onClick={() => setTab('users')}>発表者一覧</Button>
        <Button variant={tab === 'participants' ? 'default' : 'outline'} size="sm" onClick={() => setTab('participants')}>参加者一覧</Button>
        <Button variant={tab === 'stats' ? 'default' : 'outline'} size="sm" onClick={() => setTab('stats')}>統計</Button>
        <Button variant={tab === 'comments' ? 'default' : 'outline'} size="sm" onClick={() => { setTab('comments'); if (!openCommentsForCandidate && candidates[0]) setOpenCommentsForCandidate(candidates[0].id); }}>コメント管理</Button>
      </div>

      {tab === 'stats' && (
      <>
      <div className="flex items-center justify-end gap-2">
        <Button onClick={exportToCSV} variant="outline" disabled={candidates.length === 0}>
          <Download className="h-4 w-4 mr-2" /> 発表者CSV
        </Button>
        <Button onClick={exportEvaluationsCSV} variant="outline" disabled={candidates.length === 0}>
          <Download className="h-4 w-4 mr-2" /> 評価明細CSV
        </Button>
        <Button onClick={exportEvaluatorsCSV} variant="outline">
          <Download className="h-4 w-4 mr-2" /> 参加者CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">総評価数</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalEvaluations}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">平均評価</p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.averageRating.toFixed(1)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <Heart className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">総いいね数</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalLikes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">参加希望数</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalParticipationInterest}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">候補者数</p>
            <p className="text-2xl font-bold text-foreground">{candidates.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">参加者数</p>
            <p className="text-2xl font-bold text-foreground">{evaluatorTotal}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">コメント総数</p>
            <p className="text-2xl font-bold text-foreground">{Object.values(commentCountsByCandidate).reduce((a,b)=>a+b,0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">閲覧総数</p>
            <p className="text-2xl font-bold text-foreground">{Object.values(viewsByCandidate).reduce((a,b)=>a+b,0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Rankings & Trends */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>ホット度</CardTitle>
                <CardDescription>期間内のコメント・いいね・星変更・参加希望・閲覧</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">期間:</label>
                <select className="h-9 border rounded-md px-2 bg-background" value={timeRangeHours} onChange={(e) => setTimeRangeHours(parseInt(e.target.value))}>
                  <option value={1}>1時間</option>
                  <option value={3}>3時間</option>
                  <option value={6}>6時間</option>
                  <option value={24}>24時間</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent style={{ height: 300 }}>
            {seriesLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="ts" minTickGap={20} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="comments" name="コメント" stroke="#0ea5e9" dot={false} />
                  <Line type="monotone" dataKey="likes" name="いいね" stroke="#ef4444" dot={false} />
                  <Line type="monotone" dataKey="ratings" name="星変更" stroke="#f59e0b" dot={false} />
                  <Line type="monotone" dataKey="participates" name="参加希望" stroke="#10b981" dot={false} />
                  <Line type="monotone" dataKey="views" name="閲覧" stroke="#64748b" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
          {/* Hot candidates list */}
          <div className="px-6 pb-4">
            <div className="mt-3">
              <p className="text-sm text-muted-foreground mb-2">期間内で活動が多い研究（上位5件）</p>
              <ul className="space-y-1 text-sm">
                {hotByCandidate.slice(0, 5).map((r, idx) => (
                  <li key={r.id} className="flex items-center justify-between">
                    <span className="truncate mr-2">{idx + 1}. {r.topic || r.name}</span>
                    <span className="text-muted-foreground">
                      合計 {r.total}（💬{r.comments} ❤️{r.likes} ⭐{r.ratings} 🙋{r.participates}{r.views ? ` 👁️${r.views}` : ''}）
                    </span>
                  </li>
                ))}
                {hotByCandidate.length === 0 && (
                  <li className="text-xs text-muted-foreground">期間内の活動はありません。</li>
                )}
              </ul>
            </div>
          </div>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>平均評価ランキング</CardTitle>
            <CardDescription>星&gt;0の平均で上位10件</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {([...candidates]
                .map((c: any) => ({
                  c,
                  stats: (() => {
                    const rated = (c.evaluations || []).filter((e: any) => (e.rating || 0) > 0);
                    const count = rated.length;
                    const avg = count > 0 ? (rated.reduce((s: number, e: any) => s + Math.max(0, Math.min(5, e.rating || 0)), 0) / count) : 0;
                    return { avg, count };
                  })()
                }))
                .filter(x => x.stats.count > 0)
                .sort((a, b) => b.stats.avg - a.stats.avg)
                .slice(0, 10)
                .map(({ c, stats }, idx) => (
                  <li key={c.id} className="flex items-center justify-between">
                    <span className="truncate mr-2">{idx + 1}. {c.research_topic}</span>
                    <span className="text-muted-foreground">{stats.avg.toFixed(1)} / 5（{stats.count}件）</span>
                  </li>
                )))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>コメント数ランキング</CardTitle>
            <CardDescription>総コメント数で上位10件</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {([...candidates]
                .map((c: any) => ({ c, count: commentCountsByCandidate[c.id] || 0 }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10)
                .map(({ c, count }, idx) => (
                  <li key={c.id} className="flex items-center justify-between">
                    <span className="truncate mr-2">{idx + 1}. {c.research_topic}</span>
                    <span className="text-muted-foreground">{count}件</span>
                  </li>
                )))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ホットな発表（直近2時間）</CardTitle>
            <CardDescription>コメント増加の多い順 上位10件</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {([...candidates]
                .map((c: any) => ({ c, count: trendingCommentCounts[c.id] || 0 }))
                .filter(x => x.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, 10)
                .map(({ c, count }, idx) => (
                  <li key={c.id} className="flex items-center justify-between">
                    <span className="truncate mr-2">{idx + 1}. {c.research_topic}</span>
                    <span className="text-muted-foreground">{count}件/2時間</span>
                  </li>
                )))}
              {Object.keys(trendingCommentCounts).length === 0 && (
                <p className="text-xs text-muted-foreground">最近2時間で新しいコメントはありません。</p>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>参加希望者が多い発表</CardTitle>
            <CardDescription>参加希望数で上位10件</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {([...candidates]
                .map((c: any) => ({ c, count: (c.evaluations || []).filter((e: any) => !!e.want_to_participate).length }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10)
                .map(({ c, count }, idx) => (
                  <li key={c.id} className="flex items-center justify-between">
                    <span className="truncate mr-2">{idx + 1}. {c.research_topic}</span>
                    <span className="text-muted-foreground">{count}人</span>
                  </li>
                )))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>候補者別サマリー</CardTitle>
          <CardDescription>いいね・参加希望・コメント数・閲覧数の一覧（評価数・平均は星&gt;0のみ対象）</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left py-2 px-3">研究者</th>
                <th className="text-left py-2 px-3">評価数</th>
                <th className="text-left py-2 px-3">平均評価</th>
                <th className="text-left py-2 px-3">いいね</th>
                <th className="text-left py-2 px-3">参加希望</th>
                <th className="text-left py-2 px-3">コメント</th>
                <th className="text-left py-2 px-3">閲覧</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => {
                const s = getCandidateStats(c);
                return (
                  <tr key={c.id} className="border-t">
                    <td className="py-2 px-3">{c.name}（{c.research_topic}）</td>
                    <td className="py-2 px-3">{s.evaluationCount}</td>
                    <td className="py-2 px-3">{s.averageRating.toFixed(1)}</td>
                    <td className="py-2 px-3">{s.likes}</td>
                    <td className="py-2 px-3">{s.participationInterest}</td>
                    <td className="py-2 px-3">{commentCountsByCandidate[c.id] || 0}</td>
                    <td className="py-2 px-3">{viewsByCandidate[c.id] || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>
      )}

      {tab === 'users' && (
      <Card>
        <CardHeader>
          <CardTitle>発表者一覧</CardTitle>
          <CardDescription>各候補者の評価状況と統計情報</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {candidates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">候補者が登録されていません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      研究者
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      研究課題
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      評価数
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      平均評価
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      いいね
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      参加希望
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      キースライド
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {candidates.map((candidate) => {
                    const candidateStats = getCandidateStats(candidate);
                    const keySlideUrl = getKeySlideUrl(candidate);


                    return (
                      <>
                      <tr key={candidate.id} className="hover:bg-muted/50">
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-foreground">
                              {candidate.name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {candidate.affiliation}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-foreground max-w-xs truncate">
                            {candidate.research_topic}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="secondary">
                            {candidateStats.evaluationCount}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Star className="h-4 w-4 text-yellow-500" />
                            <span className="text-sm font-medium text-foreground">
                              {candidateStats.averageRating.toFixed(1)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Heart className="h-4 w-4 text-red-500" />
                            <span className="text-sm font-medium text-foreground">
                              {candidateStats.likes}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-foreground">
                              {candidateStats.participationInterest}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {keySlideUrl ? (
                            <div className="h-12 w-16 bg-muted rounded border overflow-hidden">
                              <img
                                src={keySlideUrl}
                                alt="キースライド"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="h-12 w-16 bg-muted rounded border flex items-center justify-center">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant={openCandidateId === candidate.id ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => openEditor(candidate)}
                            >
                              {openCandidateId === candidate.id ? (
                                <>
                                  <X className="h-4 w-4 mr-1" /> 編集終了
                                </>
                              ) : (
                                <>
                                  <Edit2 className="h-4 w-4 mr-1" /> 評価編集
                                </>
                              )}
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteUser(candidate.email)}>
                              <Trash2 className="h-4 w-4 mr-1" /> ユーザー削除
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {openCandidateId === candidate.id && (
                        <tr>
                          <td colSpan={8} className="px-6 py-4 bg-muted/30">
                            <div className="space-y-6">
                              <div className="text-sm text-muted-foreground">評価のいいね/参加希望を編集できます。</div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-muted-foreground">
                                      <th className="text-left py-2">ユーザー名</th>
                                      <th className="text-left py-2">いいね</th>
                                      <th className="text-left py-2">参加希望</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(candidate.evaluations || []).map(ev => (
                                      <tr key={ev.id} className="border-t">
                                        <td className="py-2">
                                          {(() => {
                                            const info = evaluatorById[ev.evaluator_id] || { name: '' };
                                            return info.name || '不明';
                                          })()}
                                        </td>
                                        <td className="py-2">
                                          <Button variant={ev.liked ? 'default' : 'outline'} size="sm" onClick={() => updateEvaluationField(ev.id, { liked: !ev.liked })}>
                                            いいね {ev.liked ? 'On' : 'Off'}
                                          </Button>
                                        </td>
                                        <td className="py-2">
                                          <Button variant={ev.want_to_participate ? 'default' : 'outline'} size="sm" onClick={() => updateEvaluationField(ev.id, { want_to_participate: !ev.want_to_participate })}>
                                            参加 {ev.want_to_participate ? 'On' : 'Off'}
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {/* Slides management for admin */}
                              <div className="border rounded-md p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <div className="font-medium">スライド管理（管理者）</div>
                                    <div className="text-sm text-muted-foreground">4枚まで。画像をアップロードまたは差し替え、キースライドの設定ができます。</div>
                                  </div>
                                  {slideUploading[candidate.id] && (
                                    <div className="text-xs text-muted-foreground flex items-center">
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-2"></div>
                                      アップロード中...
                                    </div>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {[0,1,2,3].map((idx) => {
                                    const slide = (candidate.slide_images || []).find((s: any) => s.slide_index === idx);
                                    const isKey = candidate.key_slide_index === idx;
                                    return (
                                      <div key={idx} className={`border rounded-lg p-3 ${isKey ? 'ring-2 ring-yellow-500' : ''}`}>
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="font-medium text-sm">スライド {idx + 1}</div>
                                          {isKey && (
                                            <Badge className="bg-yellow-500 hover:bg-yellow-600">
                                              <Star className="h-3 w-3 mr-1 fill-current" /> キー
                                            </Badge>
                                          )}
                                        </div>
                                        {slide ? (
                                          <div className="space-y-3">
                                            <div className="aspect-video bg-muted rounded-md overflow-hidden">
                                              <img src={slide.image_url} alt={`スライド ${idx + 1}`} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              <Button variant="outline" size="sm" asChild>
                                                <label className="cursor-pointer">
                                                  <Upload className="h-4 w-4 mr-2" /> 画像を変更
                                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSlideForCandidate(candidate, idx, e.target.files)} />
                                                </label>
                                              </Button>
                                              {!isKey && (
                                                <Button variant="outline" size="sm" className="text-yellow-600 border-yellow-200 hover:bg-yellow-50" onClick={() => setKeySlideForCandidate(candidate.id, idx)}>
                                                  <Star className="h-4 w-4 mr-2" /> キーに設定
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="space-y-3">
                                            <div className="aspect-video bg-muted rounded-md flex items-center justify-center border-2 border-dashed border-border">
                                              <ImageIcon className="h-8 w-8 text-muted-foreground" />
                                            </div>
                                            <Button size="sm" asChild>
                                              <label className="cursor-pointer">
                                                <Upload className="h-4 w-4 mr-2" /> 画像をアップロード
                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSlideForCandidate(candidate, idx, e.target.files)} />
                                              </label>
                                            </Button>
                                            <p className="text-xs text-muted-foreground">PNG / JPG / GIF</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              {/* Candidate info form */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>研究者名</Label>
                                  <Input value={candidateEdits[candidate.id]?.name || ''} onChange={(e) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], name: e.target.value}}))} />
                                </div>
                                <div className="space-y-2">
                                  <Label>所属</Label>
                                  <Input value={candidateEdits[candidate.id]?.affiliation || ''} onChange={(e) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], affiliation: e.target.value}}))} />
                                </div>
                                <div className="space-y-2">
                                  <Label>系・部署・診療科</Label>
                                  <Input value={candidateEdits[candidate.id]?.department || ''} onChange={(e) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], department: e.target.value}}))} />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                  <Label>研究課題名</Label>
                                  <Input value={candidateEdits[candidate.id]?.research_topic || ''} onChange={(e) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], research_topic: e.target.value}}))} />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                  <Label>研究概要</Label>
                                  <Textarea rows={3} value={candidateEdits[candidate.id]?.research_overview || ''} onChange={(e) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], research_overview: e.target.value}}))} />
                                </div>
                                <div className="flex items-center space-x-2 md:col-span-2">
                                  <Checkbox id={`cand-grad-${candidate.id}`} checked={!!candidateEdits[candidate.id]?.is_grad_student} onCheckedChange={(c) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], is_grad_student: !!c}}))} />
                                  <Label htmlFor={`cand-grad-${candidate.id}`}>大学院生（修士/博士）</Label>
                                  <Input placeholder="課程（doctor/master）" className="ml-4 w-40" value={candidateEdits[candidate.id]?.grad_program || ''} onChange={(e) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], grad_program: e.target.value}}))} />
                                  <Input placeholder="学年" type="number" className="ml-2 w-24" value={candidateEdits[candidate.id]?.grad_year || ''} onChange={(e) => setCandidateEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], grad_year: e.target.value}}))} />
                                </div>
                                <div className="md:col-span-2">
                                  <Button onClick={() => saveCandidateEdits(candidate.id)}>発表者情報を保存</Button>
                                </div>
                              </div>

                              {/* Evaluator info form (if exists) */}
                              {evaluatorEdits[candidate.id] && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                  <div className="space-y-2">
                                    <Label>参加者名</Label>
                                    <Input value={evaluatorEdits[candidate.id]?.name || ''} onChange={(e) => setEvaluatorEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], name: e.target.value}}))} />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>所属</Label>
                                    <Input value={evaluatorEdits[candidate.id]?.affiliation || ''} onChange={(e) => setEvaluatorEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], affiliation: e.target.value}}))} />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>系・部署・診療科</Label>
                                    <Input value={evaluatorEdits[candidate.id]?.department || ''} onChange={(e) => setEvaluatorEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], department: e.target.value}}))} />
                                  </div>
                                  <div className="flex items-center space-x-2 md:col-span-2">
                                    <Checkbox id={`eval-grad-${candidate.id}`} checked={!!evaluatorEdits[candidate.id]?.is_grad_student} onCheckedChange={(c) => setEvaluatorEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], is_grad_student: !!c}}))} />
                                    <Label htmlFor={`eval-grad-${candidate.id}`}>大学院生（修士/博士）</Label>
                                    <Input placeholder="課程（doctor/master）" className="ml-4 w-40" value={evaluatorEdits[candidate.id]?.grad_program || ''} onChange={(e) => setEvaluatorEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], grad_program: e.target.value}}))} />
                                    <Input placeholder="学年" type="number" className="ml-2 w-24" value={evaluatorEdits[candidate.id]?.grad_year || ''} onChange={(e) => setEvaluatorEdits(prev => ({...prev, [candidate.id]: {...prev[candidate.id], grad_year: e.target.value}}))} />
                                  </div>
                                  <div className="md:col-span-2">
                                    <Button onClick={() => saveEvaluatorEdits(candidate.id)}>参加者情報を保存</Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {tab === 'participants' && (
      <Card>
        <CardHeader>
          <CardTitle>参加者一覧</CardTitle>
          <CardDescription>参加者の基本情報と活動状況</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {participants.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">参加者が見つかりません</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="text-left py-2 px-3">参加者名</th>
                  <th className="text-left py-2 px-3">ユーザー名</th>
                  <th className="text-left py-2 px-3">所属</th>
                  <th className="text-left py-2 px-3">コメント数</th>
                  <th className="text-left py-2 px-3">いいね数</th>
                  <th className="text-left py-2 px-3">参加希望数</th>
                  <th className="text-left py-2 px-3">参加希望研究課題名</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="py-2 px-3">{p.name}</td>
                    <td className="py-2 px-3">{p.name}</td>
                    <td className="py-2 px-3">{p.affiliation || ''}</td>
                    <td className="py-2 px-3">{p.commentsCount || 0}</td>
                    <td className="py-2 px-3">{p.likesCount || 0}</td>
                    <td className="py-2 px-3">{p.participateCount || 0}</td>
                    <td className="py-2 px-3">
                      {p.participateTopics && p.participateTopics.length ? p.participateTopics.join(' / ') : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      )}

      {tab === 'comments' && (
      <Card>
        <CardHeader>
          <CardTitle>コメント管理</CardTitle>
          <CardDescription>発表者ごとのスレッドを閲覧・編集できます</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder="キーワード（本文・トピック）」" value={commentSearch} onChange={(e) => setCommentSearch(e.target.value)} />
            <Button variant="outline" onClick={loadDashboardData}><RefreshCw className="h-4 w-4 mr-1"/>更新</Button>
          </div>
          <div className="space-y-4 max-h-none overflow-visible md:max-h-[60vh] md:overflow-y-auto">
            {(candidates || []).map(c => {
              const topics = groupedComments[c.id] || {};
              const total = commentCountsByCandidate[c.id] || 0;
              return (
                <div key={c.id} className="border rounded-md">
                  <div className="flex items-center justify-between p-3 bg-muted/30">
                    <div className="text-sm font-medium text-foreground">{c.name}（{c.research_topic}）</div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary">{total}件</Badge>
                      <Button variant="ghost" size="sm" onClick={() => setOpenCommentsForCandidate(openCommentsForCandidate === c.id ? null : c.id)}>
                        {openCommentsForCandidate === c.id ? '閉じる' : '開く'}
                      </Button>
                    </div>
                  </div>
                  {openCommentsForCandidate === c.id && (
                    <div className="p-3 space-y-3">
                      {Object.entries(topics).length === 0 && (
                        <p className="text-sm text-muted-foreground">コメントがありません</p>
                      )}
                      {Object.entries(topics).map(([topic, list]) => {
                        const filtered = (list as any[]).filter(cm => !commentSearch || (cm.content || '').includes(commentSearch) || (cm.topic || '').includes(commentSearch));
                        if (!filtered.length) return null;
                        return (
                          <div key={topic} className="border rounded-md p-3">
                            <div className="flex items-center justify-between">
                              <div className="font-medium flex items-center gap-2">
                                {topic === 'general' ? '一般' : topic}（{filtered.length}）
                              </div>
                            </div>
                            <div className="mt-2 space-y-2">
                              {(() => {
                                // Build threaded structure by parent_comment_id
                                const byId: Record<string, any> = {};
                                const children: Record<string, any[]> = {};
                                const roots: any[] = [];
                                const ordered = [...(filtered as any[])].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                                ordered.forEach((cm: any) => { byId[cm.id] = cm; });
                                ordered.forEach((cm: any) => {
                                  const pid = cm.parent_comment_id;
                                  if (pid && byId[pid]) {
                                    (children[pid] = children[pid] || []).push(cm);
                                  } else {
                                    roots.push(cm);
                                  }
                                });

                                const renderCmt = (cmt: any, level: number) => {
                                  // Resolve author
                                  let author = c.name as string;
                                  if (cmt.user_type === 'evaluator') {
                                    const evId = evalToEvaluator[cmt.evaluation_id as string];
                                    const info = evaluatorById[evId];
                                    author = (info?.name || '参加者') as string;
                                  }
                                  return (
                                    <div key={cmt.id} className={`${level > 0 ? 'ml-8 border-l-2 border-primary/30 pl-4' : ''} mb-3`}>
                                      <div className={'p-2 border rounded-md'}>
                                        <div className="flex items-center justify-between">
                                          <div className="text-xs text-muted-foreground flex items-center gap-2">
                                            <span className="px-2 py-0.5 rounded bg-muted text-foreground">
                                              {author}
                                            </span>
                                            {new Date(cmt.created_at).toLocaleString('ja-JP')}
                                          </div>
                                        </div>
                                        <Textarea
                                          rows={2}
                                          defaultValue={cmt.content}
                                          onBlur={async (e) => {
                                            const val = e.target.value;
                                            if (val !== cmt.content) {
                                              try {
                                                const { data: { session } } = await supabase.auth.getSession();
                                                const token = session?.access_token;
                                                if (!token) throw new Error('セッションが無効です');
                                                const resp = await fetch('/api/admin-update-comment', {
                                                  method: 'POST',
                                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                  body: JSON.stringify({ id: cmt.id, patch: { content: val } })
                                                });
                                                const j = await resp.json().catch(() => ({}));
                                                if (!resp.ok) throw new Error(j.error || '更新に失敗しました');
                                                await loadDashboardData();
                                              } catch (e: any) {
                                                setError(e.message);
                                              }
                                            }
                                          }}
                                        />
                                        <div className="mt-2 flex items-center gap-2">
                                          <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={async () => {
                                              try {
                                                const { data: { session } } = await supabase.auth.getSession();
                                                const token = session?.access_token;
                                                if (!token) throw new Error('セッションが無効です');
                                                const resp = await fetch('/api/admin-delete-comment', {
                                                  method: 'POST',
                                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                  body: JSON.stringify({ id: cmt.id })
                                                });
                                                const j = await resp.json().catch(() => ({}));
                                                if (!resp.ok) throw new Error(j.error || '削除に失敗しました');
                                                await loadDashboardData();
                                              } catch (e: any) {
                                                setError(e.message);
                                              }
                                            }}
                                          >
                                            削除
                                          </Button>
                                        </div>
                                      </div>
                                      {(children[cmt.id] || []).map((child) => renderCmt(child, level + 1))}
                                    </div>
                                  );
                                };

                                return roots.map((root) => renderCmt(root, 0));
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
